import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Send, Video, PhoneOff, CheckSquare } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/hooks/useChat';
import { useWebRTC } from '@/hooks/useWebRTC';
import { UserRole } from '@/types/auth';
import { getAuthToken } from '@/utils/auth';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MedicalRecordEditor } from '@/components/consultation/MedicalRecordEditor';
import { PatientDocumentsTab } from '@/components/documents/PatientDocumentsTab';
import { AppointmentClosureModal } from '@/components/consultation/AppointmentClosureModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getAppointmentById } from '@/services/appointments';
import { toast } from 'sonner';

export default function ChatPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const isDoctor = user?.role === UserRole.DOCTOR;
  const token = getAuthToken();
  const backFrom = (location.state as { from?: string } | null)?.from;
  const initialTab =
    searchParams.get('tab') === 'documents' ? 'documents' : 'record';
  const autoRenewDocumentId = searchParams.get('renew');

  const { messages, isConnected, sendMessage } = useChat(appointmentId, token);

  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showIncomingModal, setShowIncomingModal] = useState(false);
  const [callerInfo, setCallerInfo] = useState({ id: '', name: '' });
  const [appointment, setAppointment] = useState<any>(null);
  const [evolutionContent, setEvolutionContent] = useState('');
  const [isClosureOpen, setIsClosureOpen] = useState(false);
  const [appointmentLoadError, setAppointmentLoadError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleEvolutionChange = useCallback((content: string) => {
    setEvolutionContent(content);
  }, []);

  useEffect(() => {
    if (!appointmentId) return;
    setAppointmentLoadError(null);
    getAppointmentById(appointmentId).then((res) => {
      if (res.success && res.data) {
        setAppointment(res.data);
      } else {
        setAppointment(null);
        setAppointmentLoadError(
          res.error ||
            'Consulta não encontrada. Abra a sala a partir de Consultas ou de um atendimento ativo.',
        );
      }
    });
  }, [appointmentId]);

  const {
    localVideoRef,
    remoteVideoRef,
    isInCall,
    isCalling,
    callUser,
    answerCall,
    endCall,
  } = useWebRTC({
    userId: user?.id || '',
    onIncomingCall: (from, name) => {
      setCallerInfo({ id: from, name });
      setShowIncomingModal(true);
    },
    onCallEnded: () => {
      setShowIncomingModal(false);
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputText.trim() || isSending) return;

    try {
      setIsSending(true);
      await sendMessage(inputText);
      setInputText('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (err) {
      console.error('Falha ao enviar', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleStartCall = () => {
    if (!appointment) return;

    let targetUserId = '';
    if (user?.role === UserRole.DOCTOR) {
      targetUserId = appointment.patient?.userId || appointment.patientId;
    } else {
      targetUserId = appointment.doctor?.userId || appointment.doctorId;
    }

    if (targetUserId) {
      callUser(targetUserId, user?.name || 'Médico');
    } else {
      alert('Não foi possível identificar o participante para a chamada.');
    }
  };

  const handleBack = () => {
    if (backFrom && backFrom.startsWith('/')) {
      navigate(backFrom);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(isDoctor ? '/consultas' : '/minhas-consultas');
    }
  };

  const handleAcceptCall = () => {
    answerCall(callerInfo.id);
    setShowIncomingModal(false);
  };

  const resolvedAppointmentId = appointment?.id || appointmentId || '';
  const canCloseAppointment =
    isDoctor &&
    !!appointment &&
    !['completed', 'cancelled', 'no-show'].includes(String(appointment.status || ''));

  return (
    <div className="flex flex-col h-screen bg-neutral-50 dark:bg-neutral-900 p-2 md:p-4 lg:p-6">
      <div className={`grid h-full gap-4 ${isDoctor ? 'lg:grid-cols-12' : 'max-w-4xl mx-auto w-full'}`}>
        <Card
          className={`flex flex-col shadow-lg border-neutral-200 dark:border-neutral-800 relative overflow-hidden ${isDoctor ? 'lg:col-span-5 xl:col-span-4' : 'flex-1'}`}
        >
          {isInCall && (
            <div className="absolute inset-0 z-50 bg-black flex items-center justify-center">
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/40 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={endCall}
                  className="rounded-full w-12 h-12"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </div>
              <div className="absolute top-6 right-6 w-32 md:w-48 aspect-video bg-neutral-800 rounded-lg overflow-hidden border-2 border-white/20 shadow-xl">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {showIncomingModal && (
            <div className="absolute inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-8 max-w-sm w-full shadow-2xl border border-neutral-200 dark:border-neutral-800 text-center">
                <div className="w-20 h-20 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Video className="w-10 h-10 text-teal-600 dark:text-teal-400 animate-pulse" />
                </div>
                <h3 className="text-xl font-bold mb-2">Chamada de Vídeo</h3>
                <p className="text-muted-foreground mb-8">
                  {callerInfo.name} está te ligando para a consulta.
                </p>
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowIncomingModal(false)}
                    className="flex-1 py-6 rounded-xl"
                  >
                    Recusar
                  </Button>
                  <Button
                    onClick={handleAcceptCall}
                    className="flex-1 py-6 bg-teal-600 hover:bg-teal-700 rounded-xl"
                  >
                    Atender
                  </Button>
                </div>
              </div>
            </div>
          )}

          <CardHeader className="flex flex-row items-center justify-between border-b pb-4 shrink-0 gap-2">
            <div className="flex items-center min-w-0">
              <Button variant="ghost" size="icon" onClick={handleBack} className="mr-2 shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex flex-col min-w-0">
                <CardTitle className="text-xl truncate">Sala de Consulta</CardTitle>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <span
                    className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
                  />
                  {isConnected ? 'Conectado' : 'Desconectado'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {!isInCall && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartCall}
                  disabled={isCalling || !appointment}
                  className="bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800"
                >
                  {isCalling ? (
                    <>Chamando...</>
                  ) : (
                    <>
                      <Video className="w-4 h-4 mr-2" /> Vídeo
                    </>
                  )}
                </Button>
              )}
              {canCloseAppointment && (
                <Button
                  size="sm"
                  onClick={() => setIsClosureOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckSquare className="w-4 h-4 mr-1.5" />
                  Encerrar
                </Button>
              )}
            </div>
          </CardHeader>

          {appointmentLoadError && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
              {appointmentLoadError}
            </div>
          )}

          <CardContent className="flex-1 p-0 overflow-hidden relative">
            <ScrollArea className="h-full p-4">
              <div className="flex flex-col gap-4 pb-4">
                {!Array.isArray(messages) || messages.length === 0 ? (
                  <div className="text-center text-muted-foreground my-8">
                    Nenhuma mensagem ainda. Inicie a conversa!
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isMe = msg.senderId === user?.id;
                    return (
                      <div
                        key={msg.id || index}
                        className={`flex flex-col max-w-[70%] ${isMe ? 'self-end' : 'self-start'}`}
                      >
                        <div
                          className={`px-4 py-2 rounded-2xl text-sm ${
                            isMe
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 rounded-bl-sm'
                          }`}
                        >
                          {msg.content}
                        </div>
                        <span
                          className={`text-[10px] text-muted-foreground mt-1 ${isMe ? 'text-right' : 'text-left'}`}
                        >
                          {format(new Date(msg.createdAt), 'HH:mm', { locale: ptBR })}
                        </span>
                      </div>
                    );
                  })
                )}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>
          </CardContent>

          <CardFooter className="border-t p-4 shrink-0">
            <form onSubmit={handleSend} className="flex w-full gap-2">
              <Input
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1"
                disabled={isSending || !isConnected}
                autoFocus
              />
              <Button type="submit" disabled={!inputText.trim() || isSending || !isConnected}>
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </CardFooter>
        </Card>

        {isDoctor && (
          <div className="lg:col-span-7 xl:col-span-8 h-full overflow-hidden flex flex-col">
            <Tabs defaultValue={initialTab} className="flex-1 flex flex-col h-full overflow-hidden">
              <div className="bg-neutral-50 dark:bg-neutral-900 border-b shrink-0 px-2 flex items-center justify-between gap-2">
                <TabsList className="bg-transparent border-b-0">
                  <TabsTrigger
                    value="record"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-teal-500 rounded-none bg-transparent"
                  >
                    Prontuário Eletrônico
                  </TabsTrigger>
                  <TabsTrigger
                    value="documents"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-teal-500 rounded-none bg-transparent"
                  >
                    Documentos
                  </TabsTrigger>
                </TabsList>
                {canCloseAppointment && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsClosureOpen(true)}
                    className="my-1 border-green-600 text-green-700 hover:bg-green-50"
                  >
                    <CheckSquare className="w-4 h-4 mr-1.5" />
                    Encerrar atendimento
                  </Button>
                )}
              </div>

              <TabsContent value="record" className="flex-1 p-0 m-0 overflow-hidden h-full">
                <MedicalRecordEditor
                  appointmentId={resolvedAppointmentId}
                  patientId={appointment?.patientId || ''}
                  onContentChange={handleEvolutionChange}
                />
              </TabsContent>

              <TabsContent value="documents" className="flex-1 p-0 m-0 overflow-hidden h-full">
                <PatientDocumentsTab
                  appointmentId={resolvedAppointmentId}
                  patientId={appointment?.patientId || ''}
                  autoRenewDocumentId={autoRenewDocumentId}
                  onDocumentGenerated={async (_fileName, type, _documentId) => {
                    const docName =
                      type === 'PRESCRIPTION'
                        ? 'Prescrição'
                        : type === 'RECIPE'
                          ? 'Receita'
                          : type === 'EXAM'
                            ? 'Pedido de exame'
                            : 'Atestado';
                    await sendMessage(`📄 ${docName} emitido para você.`);
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {isDoctor && (
        <AppointmentClosureModal
          isOpen={isClosureOpen}
          onClose={() => setIsClosureOpen(false)}
          appointmentId={resolvedAppointmentId}
          evolutionContent={
            evolutionContent?.trim()
              ? evolutionContent
              : 'Consulta encerrada na sala de atendimento.'
          }
          onSuccess={() => {
            setIsClosureOpen(false);
            toast.success('Atendimento encerrado com sucesso');
            navigate('/consultas');
          }}
        />
      )}
    </div>
  );
}
